// src/utils/sales/sales.constants.js
// Centralized sales listing lifecycle constants

/**
 * Sale listing status values
 * A sale document represents a listing; SOLD is terminal.
 */
export const SALE_STATUSES = {
  STAGED: 'Staged',
  LISTED: 'Listed',
  SOLD: 'Sold'
};

export const SALE_KIND = 'sale';

export const QUEUE_PLATFORMS = ['eBay', 'Whatnot', 'Discord'];

/**
 * Status values written before the lifecycle existed, mapped onto it
 * so existing documents need no migration.
 */
const LEGACY_STATUS_MAP = {
  staged: SALE_STATUSES.STAGED,
  active: SALE_STATUSES.LISTED,
  listed: SALE_STATUSES.LISTED,
  completed: SALE_STATUSES.SOLD,
  sold: SALE_STATUSES.SOLD
};

/**
 * Resolves any stored status value to a canonical SALE_STATUSES value
 * @param {string} status - Raw status from Firestore
 * @returns {string} Canonical status
 */
export const normalizeSaleStatus = (status) => {
  if (!status) return SALE_STATUSES.LISTED;
  return LEGACY_STATUS_MAP[String(status).toLowerCase()] || SALE_STATUSES.LISTED;
};

/**
 * @param {string} status - Raw status from Firestore
 * @returns {boolean} True when the row is still in the queue
 */
export const isStaged = (status) => normalizeSaleStatus(status) === SALE_STATUSES.STAGED;

/**
 * @param {string} status - Raw status from Firestore
 * @returns {boolean} True when the sale is still live
 */
export const isListed = (status) => normalizeSaleStatus(status) === SALE_STATUSES.LISTED;

/**
 * @param {string} status - Raw status from Firestore
 * @returns {boolean} True when the sale has completed
 */
export const isSold = (status) => normalizeSaleStatus(status) === SALE_STATUSES.SOLD;

/**
 * Sold is one-way, so only a listed sale can transition
 * @param {string} status - Raw status from Firestore
 * @returns {boolean}
 */
export const canMarkSold = (status) => isListed(status);

/**
 * Activity log action types written when a sale moves cards in or out of
 * the collection. Distinct from addedToCollection / removedFromCollection so
 * the log can filter sales separately from checklist edits.
 */
export const SALE_ACTIVITY_TYPES = {
  SOLD: 'soldFromCollection',
  RETURNED: 'returnedToCollection'
};
